/**
 * @fileoverview Notes owed (/notes-owed) — every past session with no notes.
 *
 * **A list of absences, which is why it is not a filter on Lessons.** The
 * Lessons page lists lessons that exist; a session with no notes is precisely a
 * lesson that does not. Filtering a list of records for the records that were
 * never written is not a filter, so the backlog gets its own surface, reached
 * from the calendar banner and from a line on the Lessons tab.
 *
 * Oldest first, like every lesson list (lib/lessonHistory): the backlog reads
 * in the order the sessions were taught, the one waiting longest at the top.
 *
 * Cancelled occurrences never appear — nothing was taught, so nothing is owed.
 * Neither does an unassigned one: there is no student or class to write
 * against, and the fix for that lives in Scheduler, not in a notes form.
 *
 * **The window is the scheduling cache's, not a product choice.** The mirror
 * holds a complete baseline about a month back, so `/notes-owed` can only speak
 * for that span and says so. `complete: false` means an operation didn't
 * answer, which makes "nothing owed" a claim the data cannot support — so the
 * page reports the gap instead of an all-clear.
 *
 * **Some sessions need no notes.** A trip, a concert, an assembly: ticking them
 * clears them (useInstructorNotesClear). One of an organization's classes is
 * asked about instead, and stays here marked "Clear requested" until an admin
 * answers; a session of the instructor's own is cleared at once.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { getErrorMessage } from "@/lib/api";
import { composeHrefForOccurrence } from "@/lib/schedule";
import { sortChronologically } from "@/lib/lessonHistory";
import { Card, Eyebrow, Button, Icon, ListRow, Tag } from "@/components/studio/primitives";
import useInstructorNotesClear from "@/components/studio/useInstructorNotesClear";
import { clearanceLine, clearanceTag } from "@/lib/notesClearance";

function sessionLabel(isoDay) {
  const [y, m, d] = isoDay.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function clock(timeLocal) {
  const [h, m] = (timeLocal || "").split(":").map(Number);
  if (Number.isNaN(h)) return timeLocal || "";
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

/** How long ago, in the plainest terms that stay true. */
function ago(isoDay) {
  const [y, m, d] = isoDay.split("-").map(Number);
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(y, m - 1, d)) / 86400000);
  if (days <= 1) return "yesterday";
  if (days < 7) return `${days} days ago`;
  if (days < 14) return "last week";
  return `${Math.floor(days / 7)} weeks ago`;
}

export default function NotesOwedPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      const res = await api.get("/notes-owed");
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err, "Couldn't load your outstanding notes"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sessions = useMemo(() => sortChronologically((data?.sessions || []).map((s) => ({ ...s, missing: true }))), [data]);
  const clearing = useInstructorNotesClear({ rows: sessions, onChanged: () => load({ quiet: true }), testId: "notes-owed-clear" });
  const partial = data ? data.complete === false : false;

  /* Through `composeHrefForOccurrence`, not a hand-built path: the form has to
     receive the occurrence's own identity (appointment + revision, or slot +
     date), because that stamp is what carries the note's program attribution
     back to the organization. A note written without it is invisible there and
     leaves the session sitting in this very list. */
  const write = (s) => navigate(composeHrefForOccurrence(s));

  return (
    <div className="mx-auto max-w-3xl px-4 py-8 md:px-8">
      <button
        type="button"
        onClick={() => navigate("/all-lessons")}
        className="mb-4 inline-flex items-center gap-1 font-sans text-[13px] text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
      >
        <Icon name="chevronLeft" size={15} /> Lessons
      </button>

      <h1 className="font-serif text-2xl font-semibold text-[var(--text-primary)]">Notes owed</h1>
      <p className="mt-1 font-sans text-[14px] text-[var(--text-tertiary)]">
        Sessions you taught that have no notes yet. Typed notes need no recording consent.
      </p>
      {/* The zone this list is framed in — resolved, never assumed, so even
          the UTC last resort is said out loud rather than left to be read
          as local time. */}
      {data?.timezone && (
        <p className="mt-1 font-mono text-[12px] text-[var(--text-tertiary)]" data-testid="notes-owed-timezone">
          Times in {data.timezone.replaceAll("_", " ")}
        </p>
      )}

      <div className="mt-6">
        {loading ? (
          <p className="font-sans text-[15px] text-[var(--text-tertiary)]">Loading…</p>
        ) : sessions.length === 0 ? (
          <Card data-testid="notes-owed-empty">
            <div className="flex items-center gap-3">
              <Icon
                name={partial ? "alert" : "check"}
                size={20}
                className={partial ? "text-[var(--state-danger)]" : "text-[var(--state-success)]"}
              />
              <p className="font-sans text-[15px] text-[var(--text-primary)]">
                {partial
                  ? "Nothing found, but one of your calendars didn't answer — there may be more."
                  : "Every session you've taught has notes. Nothing owed."}
              </p>
            </div>
          </Card>
        ) : (
          <>
            <Eyebrow className="mb-2">
              {sessions.length} {sessions.length === 1 ? "session" : "sessions"}
              {partial && <span className="normal-case"> · one calendar didn't answer, so there may be more</span>}
            </Eyebrow>
            <Card padding={0}>
              {clearing.toolbar}
              {sessions.map((s, i) => {
                const asked = clearanceTag(s.notes_clearance);
                return (
                  <ListRow
                    key={s.occurrence_id}
                    borderBottom={i < sessions.length - 1}
                    data-testid={`notes-owed-row-${s.occurrence_id}`}
                    leading={
                      <div className="flex shrink-0 items-center gap-3">
                        {clearing.checkbox(s)}
                        <div className="w-[5.5rem] text-right">
                          <div className="font-mono text-[13px] text-[var(--text-primary)]">{sessionLabel(s.date)}</div>
                          <div className="font-mono text-[11px] text-[var(--text-tertiary)]">{clock(s.time_local)}</div>
                        </div>
                      </div>
                    }
                    title={s.display_name}
                    subtitle={[
                      s.context_label,
                      // A venue in another zone says so on its own row.
                      s.timezone && s.timezone !== data?.timezone ? `Times in ${s.timezone.replaceAll("_", " ")}` : null,
                      clearanceLine(s.notes_clearance),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                    /* An asked row trades its meta for the tag, so the name and
                       who asked keep the room on a narrow card. */
                    meta={asked ? undefined : s.kind === "class" ? `Class · ${s.attending} attended · ${ago(s.date)}` : ago(s.date)}
                    trailing={
                      <span className="flex items-center gap-2">
                        {asked && <Tag accent>{asked.label}</Tag>}
                        <Button onClick={() => write(s)} data-testid={`write-${s.occurrence_id}`}>
                          Write notes
                        </Button>
                      </span>
                    }
                  />
                );
              })}
            </Card>
            {clearing.dialogs}
          </>
        )}
      </div>
    </div>
  );
}
